import React, { useState, useEffect } from 'react';
import { listInstances } from '../services/rdsService';
import { getSecurityGroupDetails, authorizeSecurityGroupIngress, revokeSecurityGroupIngress } from '../services/ec2Service';
import { getUserIpHistory, saveUserIpHistory } from '../services/dynamoService';
import { useAuth } from '../contexts/AuthContext';

const SecurityGroups = ({ awsCredentials }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rdsInstances, setRdsInstances] = useState([]);
  const [securityGroups, setSecurityGroups] = useState([]);
  const [currentIp, setCurrentIp] = useState('');
  const [userRules, setUserRules] = useState([]);
  const [selectedSg, setSelectedSg] = useState('');
  const [customPort, setCustomPort] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [editingRule, setEditingRule] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Engine to port mapping
  const enginePorts = {
    'postgres': 5432,
    'mysql': 3306,
    'mariadb': 3306,
    'oracle': 1521,
    'sqlserver': 1433,
    'aurora-mysql': 3306,
    'aurora-postgresql': 5432
  };

  useEffect(() => {
    loadData();
  }, [awsCredentials]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current IP
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      setCurrentIp(ipData.ip);

      // Get RDS instances
      const instances = await listInstances(awsCredentials);
      setRdsInstances(instances);

      // Get unique security groups from RDS instances
      const sgMap = new Map();
      const allUserRules = [];

      for (const instance of instances) {
        const vpcSecurityGroups = instance.VpcSecurityGroups || [];
        for (const sg of vpcSecurityGroups) {
          if (!sgMap.has(sg.VpcSecurityGroupId)) {
            const sgDetails = await getSecurityGroupDetails(sg.VpcSecurityGroupId, awsCredentials);
            sgMap.set(sg.VpcSecurityGroupId, {
              id: sg.VpcSecurityGroupId,
              name: sgDetails.GroupName,
              description: sgDetails.Description,
              vpcId: sgDetails.VpcId,
              rules: sgDetails.IpPermissions,
              instances: [instance.DBInstanceIdentifier]
            });

            // Extract user's rules (rules with current user's IP)
            for (const rule of sgDetails.IpPermissions || []) {
              for (const ipRange of rule.IpRanges || []) {
                const ruleIp = ipRange.CidrIp?.replace('/32', '');
                if (ruleIp === ipData.ip || (ipRange.Description && ipRange.Description.includes(user?.email))) {
                  allUserRules.push({
                    sgId: sg.VpcSecurityGroupId,
                    sgName: sgDetails.GroupName,
                    ip: ruleIp,
                    port: rule.FromPort,
                    description: ipRange.Description || '',
                    isMyIp: ruleIp === ipData.ip && ipRange.Description?.includes('My IP:')
                  });
                }
              }
            }
          } else {
            sgMap.get(sg.VpcSecurityGroupId).instances.push(instance.DBInstanceIdentifier);
          }
        }
      }

      setSecurityGroups(Array.from(sgMap.values()));
      setUserRules(allUserRules);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addIpToAllSecurityGroups = async () => {
    if (!currentIp) {
      setError('No IP address detected');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Remove previous "My IP" rules
      for (const rule of userRules.filter(r => r.isMyIp)) {
        try {
          await revokeSecurityGroupIngress(
            rule.sgId,
            rule.ip,
            rule.port,
            awsCredentials
          );
        } catch (err) {
          console.log(`Failed to remove old rule: ${err.message}`);
        }
      }

      // Add current IP to all security groups
      let addedCount = 0;
      for (const sg of securityGroups) {
        const uniquePorts = new Set();
        for (const instance of rdsInstances.filter(i => sg.instances.includes(i.DBInstanceIdentifier))) {
          const port = enginePorts[instance.Engine] || 3306;
          if (!uniquePorts.has(port)) {
            uniquePorts.add(port);
            const description = `My IP: ${user?.email} (${instance.Engine})`;

            try {
              await authorizeSecurityGroupIngress(
                sg.id,
                currentIp,
                port,
                description,
                awsCredentials
              );
              addedCount++;
            } catch (err) {
              console.error(`Failed to add rule to ${sg.id}:`, err);
            }
          }
        }
      }

      // Save current IP to user's history (replace old ones)
      await saveUserIpHistory(user.email, [currentIp], awsCredentials);

      setSuccess(`Successfully added ${currentIp} to ${addedCount} security group rules`);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addCustomRule = async () => {
    if (!selectedSg) {
      setError('Please select a security group');
      return;
    }

    if (!customPort) {
      setError('Please enter a port');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const description = customDescription || `Custom: ${user?.email}`;

      await authorizeSecurityGroupIngress(
        selectedSg,
        currentIp,
        parseInt(customPort),
        description,
        awsCredentials
      );

      setSuccess(`Successfully added custom rule to security group`);
      setCustomPort('');
      setCustomDescription('');
      setSelectedSg('');
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const removeRule = async (rule) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await revokeSecurityGroupIngress(
        rule.sgId,
        rule.ip,
        rule.port,
        awsCredentials
      );

      setSuccess(`Successfully removed rule from ${rule.sgName}`);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateRule = async (rule, newPort, newDescription) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Remove old rule
      await revokeSecurityGroupIngress(
        rule.sgId,
        rule.ip,
        rule.port,
        awsCredentials
      );

      // Add new rule
      await authorizeSecurityGroupIngress(
        rule.sgId,
        rule.ip,
        parseInt(newPort),
        newDescription,
        awsCredentials
      );

      setSuccess(`Successfully updated rule in ${rule.sgName}`);
      setEditingRule(null);
      await loadData();
    } catch (err) {
      setError(err.message);
      // Try to restore the old rule if update failed
      try {
        await authorizeSecurityGroupIngress(
          rule.sgId,
          rule.ip,
          rule.port,
          rule.description,
          awsCredentials
        );
      } catch (restoreErr) {
        console.error('Failed to restore rule:', restoreErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPortForSg = (sg) => {
    const instance = rdsInstances.find(i => sg.instances.includes(i.DBInstanceIdentifier));
    if (instance) {
      return enginePorts[instance.Engine] || 3306;
    }
    return 3306;
  };

  const getEngineForSg = (sg) => {
    const instance = rdsInstances.find(i => sg.instances.includes(i.DBInstanceIdentifier));
    return instance?.Engine || 'unknown';
  };

  if (loading && securityGroups.length === 0) {
    return (
      <div className="aws-loading">
        <div className="aws-spinner"></div>
        Loading security groups...
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="aws-alert aws-alert-error" style={{ marginBottom: '16px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {success && (
        <div className="aws-alert aws-alert-success" style={{ marginBottom: '16px' }}>
          <strong>Success:</strong> {success}
        </div>
      )}

      {/* Current IP Section */}
      <div className="aws-container" style={{ marginBottom: '24px', padding: '16px', background: '#f9f9f9', border: '1px solid #d5dbdb', borderRadius: '4px' }}>
        <h3 style={{ marginTop: 0 }}>Your Current IP Address</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <code style={{ fontSize: '16px', padding: '8px 12px', background: '#fff', border: '1px solid #d5dbdb', borderRadius: '4px' }}>
            {currentIp || 'Loading...'}
          </code>
          <button
            className="aws-button aws-button-primary"
            onClick={addIpToAllSecurityGroups}
            disabled={loading || !currentIp}
          >
            {loading ? 'Adding...' : 'Add My IP to All RDS Security Groups'}
          </button>
        </div>
      </div>

      {/* Your Security Group Rules */}
      <div className="aws-container" style={{ marginBottom: '24px', padding: '16px', background: '#f9f9f9', border: '1px solid #d5dbdb', borderRadius: '4px' }}>
        <h3 style={{ marginTop: 0 }}>Your Security Group Rules</h3>

        {userRules.length === 0 ? (
          <p style={{ color: '#687078', margin: 0 }}>No rules found. Click "Add My IP to All RDS Security Groups" to add access rules.</p>
        ) : (
          <table className="aws-table" style={{ marginTop: '12px' }}>
            <thead>
              <tr>
                <th>Security Group</th>
                <th>IP Address</th>
                <th>Port</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userRules.map((rule, index) => (
                <tr key={`${rule.sgId}-${rule.ip}-${rule.port}-${index}`}>
                  <td>{rule.sgName}</td>
                  <td>
                    <code>{rule.ip}/32</code>
                    {rule.isMyIp && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#037f0c', fontWeight: 'bold' }}>
                        (Current IP)
                      </span>
                    )}
                  </td>
                  <td>
                    {editingRule?.sgId === rule.sgId && editingRule?.port === rule.port ? (
                      <input
                        type="number"
                        className="aws-input"
                        style={{ width: '80px' }}
                        defaultValue={rule.port}
                        min="1"
                        max="65535"
                        id={`edit-port-${index}`}
                      />
                    ) : (
                      rule.port
                    )}
                  </td>
                  <td>
                    {editingRule?.sgId === rule.sgId && editingRule?.port === rule.port ? (
                      <input
                        type="text"
                        className="aws-input"
                        defaultValue={rule.description}
                        id={`edit-desc-${index}`}
                      />
                    ) : (
                      rule.description
                    )}
                  </td>
                  <td>
                    {rule.isMyIp ? (
                      <button
                        className="aws-button aws-button-danger"
                        onClick={() => removeRule(rule)}
                        disabled={loading}
                        style={{ fontSize: '12px', padding: '4px 12px' }}
                      >
                        Remove
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {editingRule?.sgId === rule.sgId && editingRule?.port === rule.port ? (
                          <>
                            <button
                              className="aws-button aws-button-primary"
                              onClick={() => {
                                const newPort = document.getElementById(`edit-port-${index}`).value;
                                const newDesc = document.getElementById(`edit-desc-${index}`).value;
                                updateRule(rule, newPort, newDesc);
                              }}
                              disabled={loading}
                              style={{ fontSize: '12px', padding: '4px 12px' }}
                            >
                              Save
                            </button>
                            <button
                              className="aws-button aws-button-secondary"
                              onClick={() => setEditingRule(null)}
                              disabled={loading}
                              style={{ fontSize: '12px', padding: '4px 12px' }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="aws-button aws-button-secondary"
                              onClick={() => setEditingRule(rule)}
                              disabled={loading}
                              style={{ fontSize: '12px', padding: '4px 12px' }}
                            >
                              Edit
                            </button>
                            <button
                              className="aws-button aws-button-danger"
                              onClick={() => removeRule(rule)}
                              disabled={loading}
                              style={{ fontSize: '12px', padding: '4px 12px' }}
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Custom Rule Section */}
      <div className="aws-container" style={{ marginTop: '24px', padding: '16px', background: '#f9f9f9', border: '1px solid #d5dbdb', borderRadius: '4px' }}>
        <h3 style={{ marginTop: 0 }}>Add Custom Rule</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: '12px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 'bold' }}>
              Security Group
            </label>
            <select
              className="aws-select"
              value={selectedSg}
              onChange={(e) => {
                setSelectedSg(e.target.value);
                const sg = securityGroups.find(s => s.id === e.target.value);
                if (sg) {
                  setCustomPort(getDefaultPortForSg(sg).toString());
                  setCustomDescription(`Custom: ${getEngineForSg(sg)} access`);
                }
              }}
            >
              <option value="">Select security group...</option>
              {securityGroups.map(sg => (
                <option key={sg.id} value={sg.id}>
                  {sg.name} ({sg.id}) - {getEngineForSg(sg)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 'bold' }}>
              Port
            </label>
            <input
              type="number"
              className="aws-input"
              value={customPort}
              onChange={(e) => setCustomPort(e.target.value)}
              placeholder="e.g., 5432"
              min="1"
              max="65535"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 'bold' }}>
              Description (Optional)
            </label>
            <input
              type="text"
              className="aws-input"
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              placeholder="Rule description"
            />
          </div>

          <button
            className="aws-button aws-button-primary"
            onClick={addCustomRule}
            disabled={loading || !selectedSg || !customPort}
          >
            Add Rule
          </button>
        </div>
      </div>

      {/* Security Groups Table */}
      <h3 style={{ marginTop: '32px' }}>All RDS Security Groups ({securityGroups.length})</h3>

      {securityGroups.length === 0 ? (
        <div className="aws-empty-state">
          <div className="aws-empty-state-icon">🔒</div>
          <h3>No RDS security groups found</h3>
          <p>No RDS instances with security groups were found.</p>
        </div>
      ) : (
        <table className="aws-table">
          <thead>
            <tr>
              <th>Security Group ID</th>
              <th>Name</th>
              <th>Description</th>
              <th>RDS Instances</th>
              <th>Engine</th>
              <th>Default Port</th>
              <th>Inbound Rules</th>
            </tr>
          </thead>
          <tbody>
            {securityGroups.map((sg) => (
              <tr key={sg.id}>
                <td style={{ fontFamily: 'monospace' }}>{sg.id}</td>
                <td>{sg.name}</td>
                <td>{sg.description}</td>
                <td>
                  {sg.instances.map(inst => (
                    <div key={inst} style={{ fontSize: '12px' }}>{inst}</div>
                  ))}
                </td>
                <td>{getEngineForSg(sg)}</td>
                <td>{getDefaultPortForSg(sg)}</td>
                <td>
                  {sg.rules.length} rule{sg.rules.length !== 1 ? 's' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SecurityGroups;
